import { LoaderFunctionArgs, MetaFunction, redirect } from '@remix-run/node';
import { json, redirectDocument } from '@remix-run/node';
import { type Params, useLoaderData } from '@remix-run/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '~/api';
import { OFFICE_PUBLIC_DATA } from '~/api/apiUrlsV2';
import { OfficeCard } from '~/components/common/office';
import Footer from '~/components/layouts/footer/Footer';
import Navbar from '~/components/layouts/navigation/Navbar';
import AboutTheHost from '~/components/pages/office/AboutTheHost';
import BestDealCard from '~/components/common/office/BestDealCard';
import ImageGallery from '~/components/pages/office/ImageGallery';
import KeyInfo from '~/components/pages/office/KeyInfo';
import { MobileTopNav } from '~/components/pages/office/MobileTopNav';
import OfficeDescription from '~/components/pages/office/OfficeDescription';
import OfficeHeader from '~/components/pages/office/OfficeHeader';
import OfficeLocation from '~/components/pages/office/OfficeLocation';
import RequestVisitCard from '~/components/pages/office/RequestVisitCard';
import Services from '~/components/pages/office/Services';
import { generateBreadcrumbsData, getUuidLabel } from '~/components/utils/officeData';
import type { CoreOfficeDataType } from '~/types/offices';
import type { HostDataType } from '~/types/userTypes';
import mixpanelTrack from '~/hooks/useMixPanel';
import OfficeAvailabilityBanner from '~/components/pages/office/OfficeAvailabilityBanner';
import { decodeTokenFromString, tokenStringFromCookieHeader } from '~/services/session.server';
import OfficeVersionBanner from '~/components/pages/office/OfficeVersionBanner';
import { ga4TrackViewItem } from '~/hooks/useGoogleTagManager';
import {
  hasSpecialSigns,
  isStringParamURLConform,
  makeStringParamURLConform,
  removeSpecialSigns,
} from '~/components/utils/text';
import { BreadcrumbNavigation } from '~/components/common/BreadcrumbNavigation';
import type { SupportedLanguage } from '~/config/i18n';
import { useLanguageParam } from '~/config/i18n';
import PhoneVerificationBanner from '~/components/common/phone-verification/PhoneVerificationBanner';
import { useUser } from '~/modules/useUser';
import OfficeAvailabilityCard from '~/components/pages/office/OfficeAvailabilityCard';
import BottomBar from '~/components/pages/office/BottomBar';
import mixpanelSSTrack from '~/hooks/useMixpanelSS';

function redirectIfParamsNotConform(param: Params, searchParams: URLSearchParams) {
  if (param.city && !isStringParamURLConform(param.city)) {
    const conformCity = makeStringParamURLConform(param.city);
    const redirectURL = `/${param.lang}/${param.page}/${conformCity}/${param.zipCode}/${param.seats}/${param.flex}/${param.id}`;
    throw redirect(`${redirectURL}?${searchParams.toString()}`, 301);
  }
  // TODO validate all the params: page, city, zipCode, seats, flex
}

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { searchParams } = new URL(request.url);
  redirectIfParamsNotConform(params, searchParams);

  const paramUrl = Object.values(params).join('/');
  const referer = request.headers.get('Referer') || '';
  const lang = params.lang ? (params.lang as SupportedLanguage) : 'fr';
  const cookieHeader = request.headers.get('Cookie');
  const token = tokenStringFromCookieHeader(cookieHeader || '');
  const decodedToken = token ? decodeTokenFromString(token) : null;
  const url = new URL(request.url);
  const isAdmin = decodedToken?.isAdmin || false;
  const showOriginal = url.searchParams.get('showOriginal') == 'true' && isAdmin;

  if (hasSpecialSigns(paramUrl)) {
    throw redirectDocument(`/${removeSpecialSigns(paramUrl)}?${new URLSearchParams(url.searchParams).toString()}`, 301);
  }

  try {
    const { data } = await axiosInstance.get<{
      office: CoreOfficeDataType;
      hostData: HostDataType;
    }>(OFFICE_PUBLIC_DATA(params.id, showOriginal), {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    return json({ ...data, referer: referer, lang, showOriginal, isAdmin });
  } catch (error: any) {
    throw new Response(null, {
      status: error.response.status,
      statusText: error.response.statusText,
    });
  }
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data === undefined) return [];
  const lang = data.lang;
  const title = data.office.metaTitles[lang];
  const description = data.office.metaDescriptions[lang];
  const canonicalUrl = data.office.canonicalUrl[lang];
  const coverImageData = data.office.pictures && data.office.pictures.length > 0 && data.office.pictures[0];
  const robotsContent = data.office.publicationState == 'published_off_market' ? 'noindex, nofollow' : 'index';
  return [
    { title: title },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    { tagName: 'link', rel: 'alternate', hrefLang: 'fr', href: data.office.canonicalUrl['fr'] },
    { tagName: 'link', rel: 'alternate', hrefLang: 'en', href: data.office.canonicalUrl['en'] },
    { tagName: 'link', rel: 'alternate', hrefLang: 'x-default', href: data.office.canonicalUrl['en'] },
    {
      tagName: 'link',
      rel: 'icon',
      sizes: '32x32',
      href: 'https://www.fiveoffices.com/favicon-32x32.png',
    },
    { tagName: 'link', rel: 'icon', sizes: '16x16', href: 'https://www.fiveoffices.com/favicon-16x16.png' },
    { tagName: 'link', rel: 'mask-icon', href: 'https://www.fiveoffices.com/safari-pinned-tab.svg', color: '#1c1a1f' },
    { tagName: 'link', rel: 'manifest', href: 'https://www.fiveoffices.com/site.webmanifest' },
    { name: 'google', content: 'notranslate' },
    { name: 'msapplication-TileColor', content: '#1c1a1f' },
    { name: 'og:site_name', content: 'Fiveoffices' },
    { name: 'og:type', content: 'website' },
    { name: 'og:title', content: title },
    { name: 'twitter:title', content: title },
    { name: 'description', content: description },
    { name: 'og:description', content: description },
    { name: 'twitter:description', content: description },
    { name: 'twitter:card', content: description },
    { name: 'locale', content: lang },
    { name: 'twitter:image', content: coverImageData ? coverImageData.url : '' },
    { name: 'og:locale', content: lang },
    { name: 'og:image', content: coverImageData ? coverImageData.url : '' },
    { name: 'og:image:url', content: coverImageData ? coverImageData.url : '' },
    { name: 'og:image:width', content: coverImageData ? coverImageData.width : '' },
    { name: 'og:image:height', content: coverImageData ? coverImageData.height : '' },
    { name: 'robots', content: robotsContent },
  ];
};

export default function OfficeShow() {
  const { office, hostData, referer, showOriginal, isAdmin } = useLoaderData<typeof loader>();
  const userSession = useUser();
  const { t } = useTranslation();
  const lang = useLanguageParam();

  const navbarConfig = {
    showDesktopSearch: true,
    showMobileSearch: false,
    showMobileHeader: false,
    showBottomNav: false,
  };

  const showAboutHostSection =
    !!hostData.businessSector && !!hostData.companySize && !!hostData.teamDescription.collaboration;

  const { countryCode, city, zipCode } = office.addressDetails;
  const items = generateBreadcrumbsData(
    'offices',
    city,
    countryCode,
    zipCode,
    lang as SupportedLanguage,
    t,
    office.title[lang],
  );

  useEffect(() => {
    mixpanelTrack('E.detail_page_load');
    mixpanelSSTrack('E.detail_page_load_sst');
    ga4TrackViewItem(lang, office, hostData);
  }, [hostData, lang, office]);

  return (
    <div className="office-page">
      <Navbar configuration={navbarConfig} />
      <MobileTopNav
        office={office}
        referer={referer || `/${lang}`}
      />
      {office.publicationState == 'unpublished' && <OfficeAvailabilityBanner />}
      {office.publicationState == 'waiting_for_approval' && isAdmin && office.hasActiveVersion && (
        <OfficeVersionBanner original={showOriginal} />
      )}

      {!!userSession?.userId && <PhoneVerificationBanner />}

      <ImageGallery
        office={office}
        mobileGallery={true}
      />
      <div className="container fixed-container-md mt-5">
        <div className="d-md-flex gap-8">
          <div className="office-info-column flex-grow-1">
            <OfficeHeader office={office} />
            <ImageGallery office={office} />
            <div className="d-md-none mt-4 text-2 -regular color-grey-500">{getUuidLabel(office.uuid, t)}</div>
            <OfficeHeader
              office={office}
              mobileHeader={true}
            />
            <OfficeAvailabilityCard
              extraClasses="d-md-none"
              office={office}
            />
            <KeyInfo
              office={office}
              extraClasses="mt-4 mt-md-3 pb-8"
            />
            <OfficeDescription
              descriptions={office.officeDescription}
              extraClasses="py-8 fo-border-top-sm border-grey-200"
            />
            <OfficeLocation office={office} />
            {showAboutHostSection && (
              <AboutTheHost
                hostData={hostData}
                extraClasses="pt-8 fo-border-top-sm border-grey-200"
              />
            )}
            <Services officeInfo={office.officeInfo} />
          </div>
          <div className="cta-card-column d-none d-md-flex flex-column flex-grow-1">
            <OfficeAvailabilityCard
              office={office}
              extraClasses="d-none d-md-block"
            />
            <div className="-sticky-under-navbar pt-3">
              <RequestVisitCard
                office={office}
                hostData={hostData}
              />
              {office.isBestDeal && <BestDealCard extraClasses="mt-3" />}
            </div>
          </div>

          {/* mobile cta bottom bar */}
          <div className="d-md-none">
            <BottomBar
              office={office}
              hostData={hostData}
            />
          </div>
        </div>
      </div>
      <div className="container fixed-container-md my-12">
        {!!office.similarOffices && (
          <>
            <h3 className="fo-display-3 -medium mb-6">
              {office.pro ? t('office.show.title2Coworking') : t('office.show.title2')}
            </h3>
            <div className="row g-8">
              {office.similarOffices.map((similarOffice, index) => (
                <div
                  className="col-12 col-md-6 col-lg-4 col-xl-3"
                  key={index}
                >
                  <OfficeCard office={similarOffice} />
                </div>
              ))}
            </div>
          </>
        )}
        <div className="my-12">
          <BreadcrumbNavigation items={items} />
        </div>
        {!!office.seoDescriptions[lang as 'fr' | 'en'] && (
          <div className="mt-12">
            <h3 className="text-1 -medium mb-1">{t('office.show.ourTake')}</h3>
            <p>{office.seoDescriptions[lang]}</p>
          </div>
        )}
      </div>
      <Footer extraClasses="fo-footer" />
    </div>
  );
}
